import { z } from 'zod';
import { BookmarkCollectionVisibility, SLUG_REGEX, } from './collection.js';
// ---------------------------------------------------------------------------
// Collections — POST / PATCH bodies
// ---------------------------------------------------------------------------
export const CreateCollectionRequest = z.object({
    name: z.string().min(1).max(120),
    /** Optional explicit slug. When omitted, the service derives one
     *  from `name` (lowercase + hyphenate) and appends a numeric suffix
     *  on collision. Explicit slugs must match the same regex articles
     *  use; they're per-user (the unique index is partial). */
    slug: z
        .string()
        .min(1)
        .max(96)
        .regex(SLUG_REGEX, 'slug must be lowercase letters/digits with single hyphens')
        .optional(),
    description: z.string().max(2000).nullable().optional(),
    coverFileId: z.string().uuid().nullable().optional(),
    visibility: BookmarkCollectionVisibility.optional(),
});
/** All fields optional — PATCH semantics. `slug` IS allowed here so
 *  users can rename the URL after a typo; the service rejects collisions
 *  with another of the user's collections. */
export const UpdateCollectionRequest = CreateCollectionRequest.partial();
// ---------------------------------------------------------------------------
// Bookmarks — POST / PATCH bodies
// ---------------------------------------------------------------------------
// Personal tags: tolerant on content (emoji, punctuation, multi-script
// all welcome) — they're owner-only labels with no public exposure
// (the public-collection route scrubs `tags` for non-owner viewers).
// Length-only constraint matches the AJV wire schema in
// `apps/api/src/modules/content/bookmarks/route-schemas.ts`; keeping
// the two in sync is the contract-vs-runtime invariant.
const TagsArray = z.array(z.string().min(1).max(64)).max(32);
export const AddBookmarkRequest = z.object({
    articleId: z.string().uuid(),
    /** Optional personal note to attach. */
    note: z.string().max(2000).nullable().optional(),
    /** Optional personal tags. Distinct from the article taxonomy. */
    tags: TagsArray.optional(),
});
export const UpdateBookmarkRequest = z.object({
    note: z.string().max(2000).nullable().optional(),
    tags: TagsArray.optional(),
});
/** Body shape for `POST /me/read-later/items` — same as
 *  AddBookmarkRequest minus the optional metadata, since Read Later is
 *  a one-click gesture; the SPA can edit metadata via the regular
 *  bookmark edit endpoint after the fact. */
export const AddReadLaterRequest = z.object({
    articleId: z.string().uuid(),
});
/** Body shape for the list/reader batch-state read. It intentionally
 *  returns only the viewer's own bookmark locations; article visibility
 *  is already established by the page that requested the article ids. */
export const BookmarkLocationsBatchRequest = z.object({
    articleIds: z.array(z.string().uuid()).max(100),
});
// ---------------------------------------------------------------------------
// Pagination — shared by list endpoints
// ---------------------------------------------------------------------------
export const ListBookmarksQuery = z.object({
    limit: z.coerce.number().int().min(1).max(100).optional(),
    offset: z.coerce.number().int().min(0).optional(),
});
export const ListCollectionsQuery = z.object({
    limit: z.coerce.number().int().min(1).max(200).optional(),
    offset: z.coerce.number().int().min(0).optional(),
    visibility: BookmarkCollectionVisibility.optional(),
});
//# sourceMappingURL=requests.js.map